import { useCallback, useState } from "react"
import type { CombinedError } from "urql"
import { useMutation } from "urql"

import type { LocationUpdateInput, UpdateLocationMutationMutation } from "@/graphql/codegen/graphql"
import { UpdateLocationMutationDocument } from "@/graphql/codegen/graphql"

interface UseUpdateLocationReturn {
  updatingLocation: boolean
  updateLocation: (
    input: LocationUpdateInput,
    refetchAdditionalData: () => void
  ) => Promise<UpdateLocationMutationMutation | CombinedError | undefined>
}

export const useUpdateLocation = (): UseUpdateLocationReturn => {
  const [fetching, setFetching] = useState(false)
  const [, executeMutation] = useMutation<UpdateLocationMutationMutation, LocationUpdateInput>(
    UpdateLocationMutationDocument
  )

  const updateLocation = useCallback(
    async (
      input: LocationUpdateInput,
      refetchAdditionalData?: () => void
    ): Promise<UpdateLocationMutationMutation | CombinedError | undefined> => {
      setFetching(true)
      const { data, error } = await executeMutation({ input })

      if (error || data?.location.update.__typename === "LocationUpdateFailure") {
        setFetching(false)
        return error
      }

      try {
        await refetchAdditionalData?.()
      } catch (error) {
        console.error("Failed to refetch Additional Data", error)
      }
      setFetching(false)
      return data
    },
    [executeMutation]
  )

  return { updatingLocation: fetching, updateLocation }
}
